// The 25 SHAPE deities: five per sector, each owning one kind of Endeavour.
// Standing full-body figures are built by art-masters/v10/deities/shape/build_app_figures.py
// into assets/v10/deities/shape/<id>.png (+ sizes.json for aspect ratios).
// Names/roles follow the SHAPE roster; `themes` are lowercase keywords used to
// match a user's free-text ATLAS endeavour to the sector's deity.
import type { ImageSourcePropType } from 'react-native';
import type { Sector } from '../domain/olmps';
import SIZES from '../../assets/v10/deities/shape/sizes.json';

export interface ShapeDeity {
  id: string;
  name: string;
  sector: Sector;
  role: string;
  themes: string[];
}

const d = (name: string, sector: Sector, role: string, themes: string[]): ShapeDeity => ({
  id: name.toLowerCase(),
  name,
  sector,
  role,
  themes,
});

export const SHAPE_DEITIES: ShapeDeity[] = [
  // Social
  d('FILOT', 'S', 'partner and family, keeps closest people close',
    ['partner', 'family', 'dating', 'spouse', 'marriage', 'romantic', 'love', 'quality time', 'kids', 'parent']),
  d('XARIS', 'S', 'friendships, generosity, showing up',
    ['friend', 'generous', 'generosity', 'showing up', 'kindness', 'gift', 'trusted']),
  d('MONOI', 'S', 'community and belonging',
    ['community', 'belong', 'group', 'network', 'club', 'neighbour', 'neighbor', 'team']),
  d('IRENE', 'S', 'conflict and boundaries',
    ['conflict', 'boundaries', 'argument', 'peace', 'forgive', 'resolve']),
  d('PITHO', 'S', 'communication, the word that lands',
    ['communication', 'communication skills', 'confidence', 'speak', 'conversation', 'listen', 'persuade']),

  // Health
  d('HYPNOS', 'H', 'sleep and recovery/rest',
    ['sleep', 'restorative', 'insomnia', 'bedtime', 'rest and recovery', 'fatigue']),
  d('HYGA', 'H', 'nutrition, diet, energy',
    ['nutrition', 'nutritious', 'diet', 'energetic', 'energy', 'healthy', 'weight', 'eating', 'food']),
  d('KESO', 'H', 'healing, injury and illness recovery',
    ['recovery', 'injury', 'illness', 'healing', 'health issue', 'recurring', 'pain', 'chronic condition']),
  d('BIA', 'H', 'exercise, movement, strength',
    ['exercise', 'movement', 'workout', 'strength', 'fitness', 'gym', 'run', 'walk']),
  d('SYKA', 'H', 'stress and mental health, calm',
    ['stress', 'calm', 'anxiety', 'mental health', 'relax', 'burnout']),

  // Admin
  d('URAX', 'A', 'home and chores, living space',
    ['living space', 'home', 'chores', 'clean', 'tidy', 'house', 'declutter', 'laundry']),
  d('PRAXI', 'A', 'bills and paperwork',
    ['bills', 'paperwork', 'paid', 'documents', 'inbox', 'admin tasks', 'backlog', 'files', 'accounts', 'digital']),
  d('PISTIS', 'A', 'appointments and deadlines, reliability',
    ['deadlines', 'appointments', 'on time', 'reliable', 'misses', 'punctual']),
  d('PRONO', 'A', 'planning and errands',
    ['plan', 'system', 'simple', 'low-friction', 'errands', 'schedule', 'routine']),
  d('HORKOS', 'A', 'contracts and legal obligations',
    ['compliance', 'contract', 'legal', 'obligation', 'tax', 'insurance', 'lease']),

  // Personal
  d('META', 'P', 'learning and growth',
    ['growth', 'personal growth', 'learn', 'study', 'skill', 'education', 'read']),
  d('FROSY', 'P', 'self-discipline and habits',
    ['discipline', 'habit', 'consistency', 'procrastinat', 'willpower', 'self-control']),
  d('ELIS', 'P', 'purpose and motivation',
    ['purpose', 'motivation', 'clarity', 'want from life', 'what i actually want', 'meaning', 'direction']),
  d('AIDOS', 'P', 'values and self-awareness',
    ['who i am', 'sense of', 'self-respect', 'boundaries', 'reflective', 'spiritual', 'emotional', 'inner', 'values', 'identity', 'self-awareness']),
  d('PYDA', 'P', 'hobbies, play, creativity',
    ['creative', 'expressive', 'hobby', 'hobbies', 'play', 'art', 'music', 'just for me', 'fun']),

  // Economic
  d('PLUTUS', 'E', 'budgeting and spending',
    ['budget', 'spending', 'expenses', 'spend', 'frugal']),
  d('KERDOS', 'E', 'income and craft/earning skill',
    ['income', 'earning', 'earn', 'salary', 'side hustle', 'freelance', 'craft']),
  d('UTHEN', 'E', 'saving and investing',
    ['emergency fund', 'fund', 'saving', 'invest', 'investment', 'financial independence', 'independence', 'retire', 'wealth']),
  d('THERA', 'E', 'getting out of debt',
    ['debt', 'loan', 'owe', 'credit card', 'mortgage', 'freedom', 'depend', 'options']),
  d('AGON', 'E', 'career, ambition and competition',
    ['career', 'promotion', 'ambition', 'ambitious', 'compete', 'competition', 'advance', 'leadership', 'business']),
];

export const SHAPE_BY_ID: Record<string, ShapeDeity> = Object.fromEntries(
  SHAPE_DEITIES.map((x) => [x.id, x]),
);

/* eslint-disable @typescript-eslint/no-require-imports */
const FULL: Record<string, ImageSourcePropType> = {
  filot: require('../../assets/v10/deities/shape/filot.png'),
  xaris: require('../../assets/v10/deities/shape/xaris.png'),
  monoi: require('../../assets/v10/deities/shape/monoi.png'),
  irene: require('../../assets/v10/deities/shape/irene.png'),
  pitho: require('../../assets/v10/deities/shape/pitho.png'),
  hypnos: require('../../assets/v10/deities/shape/hypnos.png'),
  hyga: require('../../assets/v10/deities/shape/hyga.png'),
  keso: require('../../assets/v10/deities/shape/keso.png'),
  bia: require('../../assets/v10/deities/shape/bia.png'),
  syka: require('../../assets/v10/deities/shape/syka.png'),
  urax: require('../../assets/v10/deities/shape/urax.png'),
  praxi: require('../../assets/v10/deities/shape/praxi.png'),
  pistis: require('../../assets/v10/deities/shape/pistis.png'),
  prono: require('../../assets/v10/deities/shape/prono.png'),
  horkos: require('../../assets/v10/deities/shape/horkos.png'),
  meta: require('../../assets/v10/deities/shape/meta.png'),
  frosy: require('../../assets/v10/deities/shape/frosy.png'),
  elis: require('../../assets/v10/deities/shape/elis.png'),
  aidos: require('../../assets/v10/deities/shape/aidos.png'),
  pyda: require('../../assets/v10/deities/shape/pyda.png'),
  plutus: require('../../assets/v10/deities/shape/plutus.png'),
  kerdos: require('../../assets/v10/deities/shape/kerdos.png'),
  uthen: require('../../assets/v10/deities/shape/uthen.png'),
  thera: require('../../assets/v10/deities/shape/thera.png'),
  agon: require('../../assets/v10/deities/shape/agon.png'),
};
/* eslint-enable @typescript-eslint/no-require-imports */

/** Standing full-body figure for a SHAPE deity, with width/height aspect. */
export function shapeFullFor(id: string): { src: ImageSourcePropType; aspect: number } | undefined {
  const src = FULL[id];
  const size = (SIZES as Record<string, number[]>)[id];
  if (!src || !size) return undefined;
  return { src, aspect: size[0] / size[1] };
}
